import { entityIndex, type Entity } from './Entity'

/**
 * Sparse-set storage for one component type.
 *
 *   sparse[entityIndex] -> position in `dense`, or -1
 *   dense[i]            -> component data
 *   owners[i]           -> the entity that owns dense[i]
 *
 * O(1) add / remove / get, and `dense` has no holes so iteration is linear and
 * cache-friendly. Removal swaps the last element into the freed slot, which is
 * why component iteration order is never guaranteed.
 */
export class ComponentStore<T> {
  readonly dense: T[] = []
  readonly owners: Entity[] = []
  private sparse: Int32Array

  constructor(capacity = 1024) {
    this.sparse = new Int32Array(capacity).fill(-1)
  }

  get size(): number {
    return this.dense.length
  }

  private ensure(index: number): void {
    if (index < this.sparse.length) return
    let next = this.sparse.length
    while (next <= index) next *= 2
    const grown = new Int32Array(next).fill(-1)
    grown.set(this.sparse)
    this.sparse = grown
  }

  has(e: Entity): boolean {
    const i = entityIndex(e)
    return i < this.sparse.length && this.sparse[i] !== -1
  }

  get(e: Entity): T | undefined {
    const i = entityIndex(e)
    if (i >= this.sparse.length) return undefined
    const slot = this.sparse[i]
    return slot === -1 ? undefined : this.dense[slot]
  }

  /** Throws in dev if absent — use when the query already guarantees presence. */
  getOrThrow(e: Entity): T {
    const v = this.get(e)
    if (v === undefined) {
      throw new Error(`Component missing on entity ${entityIndex(e)}`)
    }
    return v
  }

  /** Adds or overwrites. Returns the stored value. */
  set(e: Entity, value: T): T {
    const i = entityIndex(e)
    this.ensure(i)
    const slot = this.sparse[i]
    if (slot !== -1) {
      this.dense[slot] = value
      return value
    }
    this.sparse[i] = this.dense.length
    this.dense.push(value)
    this.owners.push(e)
    return value
  }

  remove(e: Entity): boolean {
    const i = entityIndex(e)
    if (i >= this.sparse.length) return false
    const slot = this.sparse[i]
    if (slot === -1) return false

    const last = this.dense.length - 1
    if (slot !== last) {
      this.dense[slot] = this.dense[last]
      this.owners[slot] = this.owners[last]
      this.sparse[entityIndex(this.owners[slot])] = slot
    }
    this.dense.pop()
    this.owners.pop()
    this.sparse[i] = -1
    return true
  }

  clear(): void {
    this.dense.length = 0
    this.owners.length = 0
    this.sparse.fill(-1)
  }
}

let nextComponentId = 0

/**
 * A component type is only a descriptor: an id, its bit in the signature, and a
 * factory for default data. The actual storage lives in the `World`, so two
 * worlds never share state — a lesson learned the hard way when a headless
 * harness ran four matches in a row and the third one read the first one's
 * transforms.
 */
export interface ComponentType<T> {
  readonly id: number
  readonly name: string
  readonly word: number
  readonly mask: number
  /** Factory used when a component is added without an explicit value. */
  readonly create: () => T
}

export const SIGNATURE_WORDS = 4
export const MAX_COMPONENT_TYPES = SIGNATURE_WORDS * 32

/**
 * Declares a component type. `create` supplies default data so prefabs can add
 * a component and then patch only the fields they care about.
 */
export function defineComponent<T>(name: string, create: () => T): ComponentType<T> {
  const id = nextComponentId++
  if (id >= MAX_COMPONENT_TYPES) {
    throw new Error(
      `Component type limit reached (${MAX_COMPONENT_TYPES}); raise SIGNATURE_WORDS.`,
    )
  }
  return {
    id,
    name,
    word: id >>> 5,
    mask: 1 << (id & 31),
    create,
  }
}

/** Marker component with no data. */
export function defineTag(name: string): ComponentType<true> {
  return defineComponent<true>(name, () => true)
}
